import { Box } from "@mui/material";
import { Outlet, useParams } from "react-router-dom";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useOrganizationCheck } from "./hooks/useOrganizationCheck";
import { PlatformCheckOverview } from "~/pages/platform/check/components/PlatformCheckOverview";
import { PlatformBreadcrumbs } from "~/pages/platform/components/PlatformBreadcrumbs";
import {
  TabListItem,
  TabNavigation,
  useRouteMatch,
} from "~/components/tab-nav";
import { useScope } from "~/hooks/useScope";

export function OrganizationCheckDetailPage() {
  const { organizationScope } = useScope();
  const { policyId } = useParams<{ policyId: string }>();
  const {
    isLoading,
    hasError,
    title,
    check,
    checkScore,
    blastRadius,
    policy,
    lastScannedAt,
    policies,
    findingMrn,
    variants,
    selectedVariant,
    hasVariants,
    searchParams,
    setSearchParams,
  } = useOrganizationCheck(policyId, organizationScope?.mrn);

  if (isLoading) return <LoadingPage what="check details" />;

  if (hasError) return <LoadingFailedPage what="check details" />;

  const breadcrumbItems = [
    {
      label: "Organization Policies",
      to: `/organization/policies?${organizationScope?.params}`,
    },
    ...(policyId && policy?.name
      ? [
          {
            label: policy.name,
            to: `/organization/policies/${encodeURIComponent(policyId)}?${organizationScope?.params}`,
          },
        ]
      : []),
  ];

  const generateHref = (tab: string): string => {
    const basePath = `/organization/policies/${encodeURIComponent(policyId || "")}/check/${tab}`;
    return findingMrn
      ? `${basePath}?findingMrn=${encodeURIComponent(findingMrn)}`
      : basePath;
  };

  const tabList: TabListItem[] = [
    {
      label: "Results",
      to: generateHref("results"),
      route: "/results",
    },
    {
      label: "Remediation",
      to: generateHref("remediation"),
      route: "/remediation",
    },
  ];

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "results",
  );

  return (
    <Box>
      <PlatformBreadcrumbs
        currentPage={title}
        items={breadcrumbItems}
        showPlatformPolicies={false}
      />
      <PlatformCheckOverview
        title={title}
        isLoading={isLoading}
        check={check}
        checkScore={checkScore}
        blastRadius={blastRadius}
        lastScannedAt={lastScannedAt}
        policies={policies}
      />

      <Box sx={{ px: 5 }}>
        <TabNavigation
          {...{ id: "check-tabs", tabList, currentTab, marginBottom: 3 }}
        />
        <Outlet
          context={{
            scopeMrn: organizationScope?.mrn || "",
            findingMrn,
            policyId,
            check,
            checkScore,
            blastRadius,
            variants,
            selectedVariant,
            hasVariants,
            searchParams,
            setSearchParams,
          }}
        />
      </Box>
    </Box>
  );
}
